import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardContent, CardHeader, Grid, IconButton, Link } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { DocumentDetails, FileModel } from 'src/interfaces/new-channel-partner/channelPartner';
import { ImageDialogBox } from 'src/components/common/image-dialog';
import { PropertyList } from 'src/components/property-list';
import { PropertyListItem } from 'src/components/property-list-item';
import { ChevronDown as ChevronDownIcon } from '../../../../../icons/chevron-down';
import { ChevronLeft as ChevronLeftIcon } from '../../../../../icons/chevron-left';
import { AddKycDocuments } from '../../dialogs/addKycDocuments';

interface KycDocumentsProps {
  scope?: string;
  documentDetails: DocumentDetails[];
  addDocument: (data: DocumentDetails, selectedFile: any) => Promise<boolean>;
  deleteDocument: (data: string) => Promise<boolean>;
}

export const KycDocumentsCard: FC<KycDocumentsProps> = (props) => {
  const { documentDetails, scope, addDocument, deleteDocument } = props;
  const [displayKycInfo, setDisplayKycInfo] = useState<boolean>(true);
  const [openAddKycDialog, setOpenAddKycDialog] = useState<boolean>(false);
  const [openDocumentImageDialog, setOpenDocumentImageDialog] = useState<boolean>(false);
  const [documentImagePath, setDocumentImagePath] = useState('');
  const [fileObject, setFileObject] = useState<FileModel>();
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [mandatoryDocuments, setMandatoryDocuments] = useState<DocumentDetails[]>([]);
  const [optionalDocuments, setOptionalDocuments] = useState<DocumentDetails[]>([]);
  const align = 'horizontal';

  const previewDocument = async (document) => {
    setDocumentNumber(document.docNumber);
    setDocumentName(document.docType);
    setFileObject(document.file);
    setOpenDocumentImageDialog(true);
    setDocumentImagePath(document.file.key);
  };

  const setMandatoryAndOptionalDocuments = () => {
    setMandatoryDocuments(documentDetails.filter((doc) => doc.mandatory));
    setOptionalDocuments(documentDetails.filter((doc) => !doc.mandatory));
  };

  useEffect(() => {
    if (documentDetails.length > 0) {
      setMandatoryAndOptionalDocuments();
    }
  }, [documentDetails]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{ mt: 1 }}
      >
        <CardHeader
          titleTypographyProps={{ variant: 'h2' }}
          title="KYC Documents"
          action={(
            <>
              <Button
                size="small"
                color="primary"
                onClick={() => setOpenAddKycDialog(true)}
                variant="contained"
              >
                Add
              </Button>
              { documentDetails?.length > 0 && (
              <>
                { displayKycInfo && (
                <IconButton
                  sx={{ py: 0 }}
                  aria-label="view"
                  onClick={() => {
                    setDisplayKycInfo(false);
                  }}
                >
                  <ChevronDownIcon />
                </IconButton>
                )}
                { !displayKycInfo && (
                <IconButton
                  sx={{ py: 0 }}
                  aria-label="view"
                  onClick={() => {
                    setDisplayKycInfo(true);
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                )}
              </ >
              )}
            </>
          )}
        />
        {documentDetails?.length > 0 && displayKycInfo && (
        <CardContent>
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              md={6}
              sm={12}
              xs={12}
            >
              <PropertyList>
                {mandatoryDocuments?.filter((item) => item.file).map(
                  (document) => (
                    <PropertyListItem
                      key={document.docType}
                      align={align}
                      label={document.docType}
                    >

                      <Link
                        title="Preview Document"
                        color="primary"
                        component={RouterLink}
                        to=""
                        underline="none"
                        variant="subtitle2"
                        onClick={() => {
                          previewDocument(document);
                        }}
                      >
                        {document.docNumber?.toString()}
                      </Link>
                    </PropertyListItem>
                  )
                )}
              </PropertyList>
            </Grid>
            <Grid
              item
              md={6}
              sm={12}
              xs={12}
            >
              <PropertyList>
                {optionalDocuments?.filter((item) => item.file).map(
                  (document) => (
                    <PropertyListItem
                      key={document.docType}
                      align={align}
                      label={document.docType}
                    >
                      <Grid
                        container
                      >
                        <Link
                          title="Preview Document"
                          color="primary"
                          component={RouterLink}
                          to=""
                          underline="none"
                          variant="subtitle2"
                          onClick={() => {
                            previewDocument(document);
                          }}
                        >
                          {document.docNumber?.toString()}
                        </Link>
                      </Grid>
                    </PropertyListItem>
                  )
                )}
              </PropertyList>
            </Grid>
          </Grid>
        </CardContent>
        )}
      </Card>
      {openDocumentImageDialog && (
      <ImageDialogBox
        onClose={() => setOpenDocumentImageDialog(false)}
        open={openDocumentImageDialog}
        imagePath={documentImagePath}
        docName={documentName}
        docNumber={documentNumber}
        file={fileObject}
      />
      )}
      {openAddKycDialog && (
      <AddKycDocuments
        open={openAddKycDialog}
        scope={scope}
        onClose={() => setOpenAddKycDialog(false)}
        documentDetails={documentDetails}
        addDocument={addDocument}
        deleteDocument={deleteDocument}
      />
      )}
    </>
  );
};

KycDocumentsCard.propTypes = {
  documentDetails: PropTypes.array,
  addDocument: PropTypes.func,
  scope: PropTypes.string,
  deleteDocument: PropTypes.func
};
